import { UserAvatarBadge } from 'app/components/user-avatar-badge';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { useTheme } from '@shopify/restyle';
import { bottomNavigationBarVisibleAtom, presentTutorialOverlayAtom } from 'app/atoms';
import { optimisticUserProfilePictureAtom } from 'app/atoms/ui';
import { Column, Row } from 'app/design/primitives/box';
import type { ButtonProps } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { IconHome, IconLayoutDashboard, IconMessages, IconUser } from '@tabler/icons-react-native';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { AddSquare } from 'app/design/icons/add-square';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useOptimisticAtomState } from 'app/hooks/use-optimistic-state';
import { useCountUnreadMessages } from 'app/network/chat/message';
import { useMyProfile } from 'app/network/profile';
import { useUser, useUserTypesConfig } from 'app/network/user';
import { canUseCentralizedCreation } from 'app/screens/intro/navigator-helper';
import { NewListingPrompt, useNewListingPrompt } from 'app/screens/new/prompt';
import { LoginSheet, useLoginSheet } from 'app/screens/onboarding/login';
import { SignUpSheet, SignUpSource, useSignUpSheet } from 'app/screens/onboarding/signup';
import type { Theme } from 'app/shopify';
import { TutorialRef } from 'app/types/tutorial';
import { WhiteLabel } from 'app/types/white-labels';
import { ICON_STROKE_WIDTH, objectsAreEquivalent } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { getCurrentRouteBranchIdentity, subscribeNavigationState } from 'app/lib/root-navigation';
import { useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import type { ComponentProps, ReactNode } from 'react';
import { memo, useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

function useNavigationBarVisibility() {
  const isBottomNavigationBarVisible = useAtomValue(bottomNavigationBarVisibleAtom);
  const tutorialOverlay = useAtomValue(presentTutorialOverlayAtom);
  const currentRouteKey = useSyncExternalStore(subscribeNavigationState, getCurrentRouteBranchIdentity, () => '');
  const isPublishButtonTutorialActive =
    tutorialOverlay?.targetRef === TutorialRef.PublishButton &&
    (!tutorialOverlay.ownerRouteKey || tutorialOverlay.ownerRouteKey === currentRouteKey);
  return isBottomNavigationBarVisible || isPublishButtonTutorialActive;
}

function NavigationBar({
  insets,
  navigation,
  state,
  overlay = false,
}: Readonly<BottomTabBarProps & { overlay?: boolean }>) {
  const theme = useTheme<Theme>();

  const { isLargeUI } = useUISize();
  const isGuestWeb = useIsGuestWeb();
  const shouldShowNavigationBar = useNavigationBarVisibility();
  const expandedNavigationBarHeight = 60 + (insets.bottom || theme.spacing['spacing.general.x4']);
  const navigationBarHeight = useSharedValue(expandedNavigationBarHeight);
  const navigationBarTranslateY = useSharedValue(0);
  const navigationBarOpacity = useSharedValue(1);

  useEffect(() => {
    // The landing page scrolls underneath the bar. Keep its viewport stable
    // while the bar slides out instead of relaying out the list every frame.
    navigationBarHeight.set(
      overlay
        ? expandedNavigationBarHeight
        : withTiming(shouldShowNavigationBar ? expandedNavigationBarHeight : 0, { duration: 220 }),
    );
    navigationBarTranslateY.set(withTiming(shouldShowNavigationBar ? 0 : 120, { duration: 220 }));
    navigationBarOpacity.set(withTiming(shouldShowNavigationBar ? 1 : 0, { duration: 180 }));
  }, [
    expandedNavigationBarHeight,
    overlay,
    shouldShowNavigationBar,
    navigationBarHeight,
    navigationBarOpacity,
    navigationBarTranslateY,
  ]);

  const navigationBarHeightStyle = useAnimatedStyle(() => ({ height: navigationBarHeight.get() }));
  const navigationBarContentStyle = useAnimatedStyle(() => ({
    opacity: navigationBarOpacity.get(),
    transform: [{ translateY: navigationBarTranslateY.get() }],
  }));

  const { data: unreadMessages } = useCountUnreadMessages();
  const { showNewListingPrompt, newListingPromptState } = useNewListingPrompt();
  const { loginSheetState } = useLoginSheet();
  const { signUpSheetState } = useSignUpSheet();

  // Login sheet is only used here on platforms NOT using user-types
  const { data: userTypesConfig } = useUserTypesConfig();
  const canLoginFromHere = !userTypesConfig || userTypesConfig.length === 0;

  if (isLargeUI) {
    return null;
  }

  return (
    <>
      <Animated.View
        pointerEvents={shouldShowNavigationBar ? 'box-none' : 'none'}
        style={[
          { overflow: 'hidden' },
          overlay ? { position: 'absolute', bottom: 0, left: 0, right: 0 } : undefined,
          navigationBarHeightStyle,
        ]}
      >
        <Animated.View style={navigationBarContentStyle}>
          <Row
            alignItems="center"
            backgroundColor="surface.default"
            elevation={6}
            paddingTop="spacing.general.x4"
            pointerEvents={shouldShowNavigationBar ? 'auto' : 'none'}
            shadowColor="text.primary"
            shadowOffset={{
              width: 0,
              height: -4,
            }}
            shadowOpacity={0.08}
            shadowRadius={10}
            style={{
              paddingRight: insets.right + theme.spacing['spacing.general.x2'],
              paddingLeft: insets.left + theme.spacing['spacing.general.x2'],
              paddingBottom: insets.bottom || theme.spacing['spacing.general.x4'],
            }}
          >
            <NavigationBarTabs
              navigation={navigation}
              state={state}
              onPublish={() => showNewListingPrompt('+')}
              unreadCount={unreadMessages?.unread_count}
            />
          </Row>
        </Animated.View>
      </Animated.View>
      <NewListingPrompt state={newListingPromptState} />
      {canLoginFromHere ? <LoginSheet state={loginSheetState} /> : null}
      {isGuestWeb ? <SignUpSheet source={SignUpSource.Billboard} state={signUpSheetState} /> : null}
    </>
  );
}

function NavigationBarTabs({
  navigation,
  state,
  onPublish,
  unreadCount,
}: Pick<BottomTabBarProps, 'navigation' | 'state'> & { onPublish: () => void; unreadCount?: number }) {
  const isGuestWeb = useIsGuestWeb();
  const { data: profile } = useMyProfile();
  const { data: user, isPending: isUserPending } = useUser();
  const centralizedCreation = canUseCentralizedCreation(user?.featureFlags, isUserPending);
  const isAmbassador = useAmbassador().isAmbassador;
  const showSuperPartageur = Boolean(user?.isSuper) && !isWhiteLabel(WhiteLabel.HauteGaspesie);

  const canPublish = isGuestWeb || isFeatureEnabled(FeatureFlag.publishListings, user?.featureFlags);

  const getRouteFromState = (route: string) => state.routes.find((r) => r.name === route);

  const iconMagnifier = ({ color, focused }: { color: string; focused: boolean }) => (
    <IconHome strokeWidth={!focused ? ICON_STROKE_WIDTH : ICON_STROKE_WIDTH * 1.2} size={28} color={color} />
  );
  const iconDashboard = ({ color, focused }: { color: string; focused: boolean }) => (
    <IconLayoutDashboard strokeWidth={!focused ? ICON_STROKE_WIDTH : ICON_STROKE_WIDTH * 1.2} size={28} color={color} />
  );
  const iconAddSquare = ({ focused }: { focused: boolean }) => (
    <AddSquare
      height={46}
      width={46}
      color={centralizedCreation ? '#202737' : undefined}
      inverted={centralizedCreation && !focused}
    />
  );
  const iconMessages = ({ color, focused }: { color: string; focused: boolean }) => (
    <IconMessages strokeWidth={!focused ? ICON_STROKE_WIDTH : ICON_STROKE_WIDTH * 1.2} size={28} color={color} />
  );
  const iconAvatar = ({ color, focused }: { color: string; focused: boolean }) => (
    <IconUser strokeWidth={!focused ? ICON_STROKE_WIDTH : ICON_STROKE_WIDTH * 1.2} size={28} color={color} />
  );

  const tabs: (Omit<ComponentProps<typeof NavigationItem>, 'focused' | 'navigation' | 'state'> & { id: string })[] = [
    {
      id: 'billboard',
      icon: iconMagnifier,
      route: getRouteFromState('billboard'),
    },
    {
      id: 'management',
      authorize: true,
      icon: iconDashboard,
      route: getRouteFromState('management'),
    },
    ...(canPublish
      ? [
          {
            id: 'publish',
            authorize: !isGuestWeb,
            icon: iconAddSquare,
            route: centralizedCreation ? getRouteFromState('publish') : undefined,
            onPress: centralizedCreation ? undefined : onPublish,
            tutorialRef: TutorialRef.PublishButton,
          },
        ]
      : []),
    {
      id: 'chat',
      authorize: true,
      icon: iconMessages,
      route: getRouteFromState('chat'),
      unread_count: unreadCount,
    },
    {
      id: 'profile',
      authorize: true,
      icon: iconAvatar,
      route: getRouteFromState('profile'),
    },
  ];

  return (
    <>
      {tabs.map(({ id, ...tab }) => (
        <NavigationItem
          {...tab}
          {...{ navigation }}
          focused={tab.route?.name === state.routes[state.index]?.name}
          image={
            tab.route?.name === 'profile' && profile?.picture
              ? {
                  profilePicture: profile.picture,
                  withImpact: showSuperPartageur,
                  withAmbassador: isAmbassador,
                }
              : undefined
          }
          key={id}
        />
      ))}
    </>
  );
}

type NavigationItemProps = Pick<ButtonProps, 'authorize'> & {
  focused: boolean;
  icon: (props: { color: string; focused: boolean }) => ReactNode;
  unread_count?: number | undefined;
  navigation: BottomTabBarProps['navigation'];
  onPress?: () => void;
  route?: { name: string; key: string };
  tutorialRef?: TutorialRef;
  image?: {
    profilePicture?: string;
    withImpact: boolean;
    withAmbassador: boolean;
  };
};

function runNavigationItemAction({
  authorize,
  continueTutorial,
  onPress,
  tutorialRef,
  withAuthorize,
}: Pick<NavigationItemProps, 'authorize' | 'onPress' | 'tutorialRef'> & {
  continueTutorial: () => unknown;
  withAuthorize: (action: () => void) => void;
}) {
  const action = () => {
    onPress?.();
    if (tutorialRef) void continueTutorial();
  };

  if (authorize) {
    withAuthorize(action);
    return;
  }
  action();
}

function NavigationAvatar({
  image,
  optimisticProfilePicture,
}: Readonly<{
  image: NonNullable<NavigationItemProps['image']>;
  optimisticProfilePicture?: string;
}>) {
  const theme = useTheme<Theme>();

  return (
    <Column borderRadius="radius.lg" height={28} width={28}>
      <PCImage
        source={{ uri: optimisticProfilePicture }}
        height={28}
        width={28}
        style={{ borderRadius: theme.borderRadii['radius.md'] }}
      />
      <UserAvatarBadge
        avatarSize={28}
        size={20}
        isAmbassador={image.withAmbassador}
        isSuper={image.withImpact}
        style={{ bottom: -10, right: -4 }}
      />
    </Column>
  );
}

function NavigationUnreadBadge({ unreadCount }: Readonly<{ unreadCount: number }>) {
  const unreadBadgeHeight = 14;
  const minWidth = unreadCount > 9 ? 25 : unreadBadgeHeight * 1.2;

  return (
    <Column
      backgroundColor="icon.error"
      borderRadius="radius.sm"
      height={unreadBadgeHeight}
      justifyContent="center"
      minWidth={minWidth}
      position="absolute"
      right={17}
      top={0}
    >
      <Text fontSize={10} fontWeight="600" style={{ textAlign: 'center', color: 'white' }}>
        {Math.min(unreadCount, 99)}
      </Text>
    </Column>
  );
}

const NavigationItem = memo(
  function NavigationItemComponent({
    authorize,
    focused,
    icon,
    unread_count,
    navigation,
    onPress,
    route,
    image,
    tutorialRef,
  }: NavigationItemProps) {
    const { t } = useTranslation();
    const theme = useTheme<Theme>();
    const label = t('tabBarLabel', { ns: route?.name === 'chat' ? 'all-channels' : route?.name, defaultValue: '' });
    const { continueTutorial, viewRef } = useTutorialRef(tutorialRef);

    const optimisticProfilePicture = useOptimisticAtomState(optimisticUserProfilePictureAtom, image?.profilePicture)[0];

    const color = focused ? theme.colors['icon.brand'] : theme.colors['icon.disabled'];

    const onItemPress = () => {
      if (!route) return;

      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!focused && !event.defaultPrevented) {
        navigation.navigate(route.name, { merge: true });
      }
    };

    const withAuthorize = useAuthorize();

    const doOnPress = () =>
      runNavigationItemAction({
        authorize,
        continueTutorial,
        onPress: onPress ?? onItemPress,
        tutorialRef,
        withAuthorize,
      });
    // const avatarMarginLeft = image && (image.withImpact || image.withAmbassador) ? 's' : undefined;

    return (
      <Pressable
        collapsable={false}
        onPress={doOnPress}
        accessibilityRole={route ? 'tab' : 'button'}
        accessibilityState={{ selected: focused }}
        accessibilityLabel={label || t('publishButton', { ns: 'header' })}
        ref={viewRef}
        testID={route ? `tab-${route.name}` : onPress ? 'tab-new-listing' : undefined}
        style={{ alignItems: 'center', flex: 1, justifyContent: 'center' }}
      >
        {image ? <NavigationAvatar image={image} optimisticProfilePicture={optimisticProfilePicture} /> : null}
        {!image && icon({ color, focused })}

        {label ? <Text style={{ marginTop: 8, fontSize: 10, color }}>{label}</Text> : null}
        {unread_count ? <NavigationUnreadBadge unreadCount={unread_count} /> : null}
      </Pressable>
    );
  },
  (prevProps, nextProps) =>
    prevProps.icon === nextProps.icon &&
    prevProps.route === nextProps.route &&
    prevProps.onPress === nextProps.onPress &&
    prevProps.authorize === nextProps.authorize &&
    prevProps.focused === nextProps.focused &&
    prevProps.unread_count === nextProps.unread_count &&
    prevProps.tutorialRef === nextProps.tutorialRef &&
    Boolean(prevProps.image) === Boolean(nextProps.image) &&
    (!prevProps.image || !nextProps.image || objectsAreEquivalent(prevProps.image, nextProps.image)),
);

export { NavigationBar };
